import { useEffect, useId, useRef, useState } from 'react'
import { COLORS } from '../lib/theme'

// NPS survey card — the web version of the mobile "We want your feedback!"
// flow, docked in the bottom-right corner so it never blocks the main flow.
// On entrance the app background blurs for a moment to draw the eye to the
// card, then the blur lifts and the user can keep working; the card stays
// available to rate, skip, or close (✕). Slider colors follow the NPS
// category of the current score (detractor 0–6 = error, passive 7–8 =
// tertiary, promoter 9–10 = primary): the inactive track shows all three
// category areas as containers, the active track and thumb take the
// category's full color. The primary action stays disabled until the slider
// has been moved; promoters submit directly, while detractors and passives
// get a follow-up free-text step.

export type NpsCategory = 'detractor' | 'passive' | 'promoter'

export function npsCategory(score: number): NpsCategory {
  if (score <= 6) return 'detractor'
  if (score <= 8) return 'passive'
  return 'promoter'
}

const CATEGORY_STYLE: Record<NpsCategory, { active: string; on: string; emoji: string }> = {
  detractor: { active: COLORS.error, on: COLORS.onError, emoji: '🙁' },
  passive: { active: COLORS.tertiary, on: COLORS.onTertiary, emoji: '😐' },
  promoter: { active: COLORS.primary, on: COLORS.onPrimary, emoji: '🙂' },
}

// Inactive-track gradient. The category boundaries sit at 6.5/10 and 8.5/10, so
// each colour holds flat until ~4% before its boundary and has arrived ~4% after
// it — the zones stay readable, but the joins are blended rather than hard cuts.
//
// Hard cuts were fine on the light palette, where all three container tints are
// near-white and differ only in hue. On dark they are not: error/tertiary
// containers are mid-tone (#a1342e, #a03c16) while the primary container is
// nearly the card colour (#243e31 on a #2b302e card), so the promoter third of
// the track visually vanished and the two left bands read as stripes. Blending
// removes the stripe effect and lets the dark green fade in instead of starting
// at an invisible edge.
const INACTIVE_TRACK_GRADIENT = `linear-gradient(90deg,
  ${COLORS.errorContainer} 0%,
  ${COLORS.errorContainer} 61%,
  ${COLORS.tertiaryContainer} 69%,
  ${COLORS.tertiaryContainer} 81%,
  ${COLORS.primaryContainer} 89%,
  ${COLORS.primaryContainer} 100%)`

// Entrance choreography: the backdrop blur fades in fast, holds for about a
// second, and releases — pointer-events stay off the whole time, so the user
// is never actually blocked, just nudged to look at the corner.
const HIGHLIGHT_MS = 1700
const NPS_CSS = `
@keyframes wh-nps-spot { 0% { opacity: 0; } 15% { opacity: 1; } 65% { opacity: 1; } 100% { opacity: 0; } }
@keyframes wh-nps-card-in { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: none; } }
`

function useNpsStyles() {
  useEffect(() => {
    if (document.getElementById('wh-nps-styles')) return
    const el = document.createElement('style')
    el.id = 'wh-nps-styles'
    el.textContent = NPS_CSS
    document.head.appendChild(el)
  }, [])
}

export type NpsStep = 'score' | 'feedback'

/** How the user left the card. */
export type NpsCancelMethod = 'close' | 'skip' | 'escape'

export interface NpsCancelContext {
  /** The score at the moment of leaving; null if the slider was never moved. */
  score: number | null
  /** Which step they were on. */
  step: NpsStep
  method: NpsCancelMethod
}

export interface NpsDialogProps {
  onSubmit: (score: number, feedback?: string) => void
  /**
   * Called when the user leaves without completing the current step: the header
   * ✕, either skip button, or Escape.
   *
   * Carries context because the cases mean different things to the caller.
   * Leaving the score step is a dismissal; skipping the comment step still
   * leaves a score worth recording, and mobile records it (with a null
   * description) rather than discarding it. This dialog owns the score→comment
   * transition internally, so only it knows which step the user was on — and
   * only it knows which control they used, which the dismissMethod analytics
   * property needs.
   *
   * A zero-argument handler remains valid — the argument is simply ignored by
   * callers that do not need it.
   */
  onCancel: (context: NpsCancelContext) => void
  /**
   * Open directly on the free-text step — used for the OPTIONAL feedback a
   * promoter can add after their rating was already submitted. Requires
   * `initialScore` (the submitted score, carried through to onSubmit).
   */
  initialStep?: NpsStep
  initialScore?: number
  /** Set false to skip the entrance blur (e.g. when reopening for optional
   *  feedback — the user is already looking at this corner). */
  highlightOnOpen?: boolean
  /** Strings — English defaults, overridable by a localized host. */
  title?: string
  question?: string
  sentimentTexts?: Record<NpsCategory, string>
  followUpTitle?: string
  followUpPlaceholder?: string
  skipLabel?: string
  nextLabel?: string
  submitLabel?: string
  notLikelyLabel?: string
  veryLikelyLabel?: string
}

// Fraction of the track (0–1) where a score's thumb centre sits.
const frac = (score: number) => score / 10

export function NpsDialog({
  onSubmit,
  onCancel,
  initialStep = 'score',
  initialScore,
  highlightOnOpen = true,
  title = 'We want your feedback!',
  question = 'How likely are you to recommend WeHunt to a friend?',
  sentimentTexts = {
    detractor: 'I am unhappy.\nI will not recommend.',
    passive: 'I am neutral.\nI may recommend.',
    promoter: 'I am happy.\nI will recommend.',
  },
  followUpTitle = 'Help us help you. What can we do better?',
  followUpPlaceholder = 'Please let us know',
  skipLabel = 'Skip',
  nextLabel = 'Next',
  submitLabel = 'Submit',
  notLikelyLabel = 'Not likely',
  veryLikelyLabel = 'Very likely',
}: NpsDialogProps) {
  useNpsStyles()
  const titleId = useId()
  const [score, setScore] = useState<number | null>(initialScore ?? null)
  const [step, setStep] = useState<NpsStep>(initialStep)
  const [feedback, setFeedback] = useState('')
  const [highlight, setHighlight] = useState(highlightOnOpen)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Every exit path goes through here, so the score/step/method context is
  // assembled in exactly one place.
  const cancel = (method: NpsCancelMethod) => onCancel({ score, step, method })

  useEffect(() => {
    const t = window.setTimeout(() => setHighlight(false), HIGHLIGHT_MS)
    return () => window.clearTimeout(t)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel('escape')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // `cancel` is recreated every render; depending on its inputs instead keeps
    // the listener bound to the current score and step.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCancel, score, step])

  useEffect(() => {
    if (step === 'feedback') textareaRef.current?.focus()
  }, [step])

  const category = score != null ? npsCategory(score) : null
  const cat = category ? CATEGORY_STYLE[category] : null

  const primaryAction = () => {
    if (score == null) return
    if (step === 'score' && npsCategory(score) !== 'promoter') {
      // Detractors and passives get the "what can we do better?" step.
      setStep('feedback')
      return
    }
    onSubmit(score, feedback.trim() || undefined)
  }

  return (
    <>
      {/* One-moment spotlight: blur the app behind the card, then release.
          pointer-events none throughout — attention cue, never a blocker. */}
      {highlight && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1390,
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            background: 'rgba(26,29,28,0.18)',
            pointerEvents: 'none',
            animation: `wh-nps-spot ${HIGHLIGHT_MS}ms ease forwards`,
          }}
        />
      )}

      <div
        role="dialog"
        aria-labelledby={titleId}
        className="wh-card"
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1400,
          width: 400,
          maxWidth: 'calc(100vw - 32px)',
          padding: '16px 20px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          boxShadow: '0 12px 36px rgba(0,0,0,0.28)',
          animation: 'wh-nps-card-in 380ms ease-out',
        }}
      >
        {/* Header row: title + close */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div id={titleId} style={{ fontWeight: 700, fontSize: 16, flex: 1 }}>
            {step === 'score' ? title : followUpTitle}
          </div>
          <button
            type="button"
            title="Close"
            onClick={() => cancel('close')}
            className="wh-btn-icon"
            style={{ width: 28, height: 28, marginTop: -4, marginRight: -8, flexShrink: 0 }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>
              ✕
            </span>
          </button>
        </div>

        {step === 'score' ? (
          <>
            <div style={{ fontSize: 13, color: COLORS.onSurfaceVariant }}>{question}</div>

            {/* Sentiment — reserved height so the card doesn't jump when the
                emoji appears on first slider move */}
            <div style={{ minHeight: 52, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              {cat && category && (
                <>
                  <span style={{ fontSize: 22, lineHeight: 1.1 }} aria-hidden>
                    {cat.emoji}
                  </span>
                  <span style={{ fontSize: 12, fontStyle: 'italic', color: COLORS.onSurfaceVariant, textAlign: 'center', whiteSpace: 'pre-line' }}>
                    {sentimentTexts[category]}
                  </span>
                </>
              )}
            </div>

            {/* Slider — segmented inactive track (detractor/passive/promoter
                container colors), category-colored active track + numbered thumb */}
            <div style={{ padding: '0 2px' }}>
              <div style={{ position: 'relative', height: 34 }}>
                {/* Inactive track: detractor -> passive -> promoter container
                    colours, blended at the 0–6 / 7–8 / 9–10 boundaries. */}
                <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 6, borderRadius: 3, background: INACTIVE_TRACK_GRADIENT }} />
                {/* Active track up to the thumb */}
                {score != null && cat && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      width: `${frac(score) * 100}%`,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: 6,
                      borderRadius: 3,
                      background: cat.active,
                      transition: 'background 150ms',
                    }}
                  />
                )}
                {/* Thumb — numbered pin in the category color; a neutral dot
                    at 0 before the first move */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: `${frac(score ?? 0) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: score != null ? 26 : 14,
                    height: score != null ? 26 : 14,
                    borderRadius: '50%',
                    background: cat?.active ?? COLORS.outline,
                    // The ring is the card colour, not white, so the thumb reads
                    // as a cut-out on both palettes. On dark, a white ring around
                    // a pale fill merged into one blob.
                    border: '2.5px solid var(--wh-card)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Paired with the fill rather than hardcoded white: every
                    // light-mode on* token IS white, so this changes nothing
                    // there, but on dark the fills are near-white (#f7dddc,
                    // #f3ded2, #9fcab2) and white-on-white hid the score.
                    color: cat?.on ?? '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    transition: 'background 150ms',
                    pointerEvents: 'none',
                  }}
                >
                  {score != null ? score : ''}
                </div>
                {/* Invisible native range on top — interaction + accessibility */}
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={1}
                  value={score ?? 0}
                  aria-label={question}
                  aria-valuetext={score != null ? String(score) : undefined}
                  onChange={(e) => setScore(Number(e.target.value))}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0 }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLORS.outline, marginTop: 1 }}>
                <span>{notLikelyLabel}</span>
                <span>{veryLikelyLabel}</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
              <button type="button" className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => cancel('skip')}>
                {skipLabel}
              </button>
              <button
                type="button"
                className="wh-btn wh-btn-primary wh-btn-sm"
                disabled={score == null}
                style={{ opacity: score == null ? 0.5 : 1, minWidth: 78, justifyContent: 'center' }}
                onClick={primaryAction}
              >
                {category === 'promoter' ? submitLabel : nextLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              className="wh-input"
              placeholder={followUpPlaceholder}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: 10 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="wh-btn wh-btn-outline wh-btn-sm" onClick={() => cancel('skip')}>
                {skipLabel}
              </button>
              <button
                type="button"
                className="wh-btn wh-btn-primary wh-btn-sm"
                style={{ minWidth: 78, justifyContent: 'center' }}
                onClick={primaryAction}
              >
                {submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}
